import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Cookie, X, Shield, BarChart3, Settings2 } from "lucide-react";

/**
 * Granular GDPR cookie consent banner.
 */
const STORAGE_KEY = "mp_cookie_consent";
const CONSENT_VERSION = "1.0";

export default function CookieConsent() {
  const { t } = useTranslation();
  const [prefs, setPrefs] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [showCustom, setShowCustom] = useState(false);
  const [draft, setDraft] = useState({ necessary: true, preferences: true, analytics: false, marketing: false });

  useEffect(() => {
    const handler = () => { setPrefs(null); setShowCustom(true); };
    window.addEventListener("open-cookie-prefs", handler);
    return () => window.removeEventListener("open-cookie-prefs", handler);
  }, []);

  if (prefs && prefs.version === CONSENT_VERSION) return null;

  const persist = (p) => {
    const full = { ...p, necessary: true, version: CONSENT_VERSION, ts: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(full));
    setPrefs(full);
    setShowCustom(false);
    // Notify analytics to (re)initialise / shutdown based on new consent
    try { window.dispatchEvent(new Event("analytics-consent-changed")); } catch { /* noop */ }
  };

  const acceptAll = () => persist({ necessary: true, preferences: true, analytics: true, marketing: true });
  const rejectAll = () => persist({ necessary: true, preferences: false, analytics: false, marketing: false });
  const saveCustom = () => persist(draft);

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[70] p-4 pointer-events-none" data-testid="cookie-consent-banner">
      <div className="max-w-5xl mx-auto bg-slate-950 border border-amber-500/40 rounded-xl shadow-2xl overflow-hidden pointer-events-auto">
        {!showCustom ? (
          <div className="p-5 sm:p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 rounded-md bg-amber-500/10 border border-amber-500/40 flex items-center justify-center flex-shrink-0">
                <Cookie size={20} className="text-amber-400"/>
              </div>
              <div className="flex-1">
                <div className="font-display font-semibold text-slate-100 mb-1">{t("cookies.title")}</div>
                <div className="text-sm text-slate-400">
                  {t("cookies.body")}{" "}
                  <Link to="/privacy" className="text-amber-400 underline hover:text-amber-300">{t("legal.privacy")}</Link>{" · "}
                  <Link to="/cookies" className="text-amber-400 underline hover:text-amber-300">{t("legal.cookies")}</Link>.
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <button onClick={rejectAll} className="px-4 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm border border-slate-700" data-testid="cookie-reject-all">
                {t("cookies.rejectAll")}
              </button>
              <button onClick={() => setShowCustom(true)} className="px-4 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm border border-slate-700" data-testid="cookie-customize">
                {t("cookies.customize")}
              </button>
              <button onClick={acceptAll} className="px-4 py-2 rounded-md bg-amber-500 hover:bg-amber-400 text-white text-sm font-medium" data-testid="cookie-accept-all">
                {t("cookies.acceptAll")}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Settings2 size={18} className="text-amber-400"/>
                <div className="font-display font-semibold text-slate-100">{t("settings.cookiePrefs")}</div>
              </div>
              <button onClick={() => setShowCustom(false)} className="p-1 rounded text-slate-500 hover:text-slate-200" data-testid="cookie-close-custom"><X size={16}/></button>
            </div>
            <div className="space-y-3">
              <CookieToggle
                title={t("cookies.necessaryTitle")}
                desc={t("cookies.necessaryBody")}
                icon={<Shield size={14} className="text-emerald-400"/>}
                checked={true}
                disabled
              />
              <CookieToggle
                title={t("cookies.preferencesTitle")}
                desc={t("cookies.preferencesBody")}
                icon={<Settings2 size={14} className="text-blue-400"/>}
                checked={draft.preferences}
                onChange={(v) => setDraft({...draft, preferences: v})}
                testid="toggle-preferences"
              />
              <CookieToggle
                title={t("cookies.analyticsTitle")}
                desc={t("cookies.analyticsBody")}
                icon={<BarChart3 size={14} className="text-cyan-400"/>}
                checked={draft.analytics}
                onChange={(v) => setDraft({...draft, analytics: v})}
                testid="toggle-analytics"
              />
              <CookieToggle
                title={t("cookies.marketingTitle")}
                desc={t("cookies.marketingBody")}
                icon={<Cookie size={14} className="text-pink-400"/>}
                checked={draft.marketing}
                onChange={(v) => setDraft({...draft, marketing: v})}
                testid="toggle-marketing"
              />
            </div>
            <div className="mt-5 flex flex-wrap gap-2 justify-end">
              <button onClick={rejectAll} className="px-4 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm border border-slate-700" data-testid="cookie-reject-from-custom">
                {t("cookies.onlyNecessary")}
              </button>
              <button onClick={saveCustom} className="px-4 py-2 rounded-md bg-amber-500 hover:bg-amber-400 text-white text-sm font-medium" data-testid="cookie-save-custom">
                {t("cookies.savePrefs")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CookieToggle({ title, desc, icon, checked, onChange, disabled, testid }) {
  return (
    <div className="p-3 rounded-md bg-slate-900/60 border border-slate-800 flex items-start gap-3">
      <div className="mt-0.5">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm text-slate-100">{title}</div>
        <div className="text-xs text-slate-400 mt-0.5">{desc}</div>
      </div>
      <button
        onClick={() => !disabled && onChange && onChange(!checked)}
        disabled={disabled}
        className={`w-10 h-6 rounded-full relative transition-colors flex-shrink-0 ${checked ? "bg-amber-500" : "bg-slate-700"} ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
        data-testid={testid}
      >
        <span className={`absolute top-0.5 ${checked ? "right-0.5" : "left-0.5"} w-5 h-5 rounded-full bg-white transition-all`}/>
      </button>
    </div>
  );
}
